import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Form, Button, Row, Col, Card, Table, Badge } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import Message from '../components/Message'
import Loader from '../components/Loader'
import { getUserDetails, updateUser } from '../actions/userActions'
import { USER_UPDATE_RESET } from '../constants/userConstants'
import { formatCurrency } from '../utils/currency'
import { Toast } from '../services/Toast'
import AdminActionBar from '../components/AdminActionBar'

const apiUrl = process.env.REACT_APP_API_URL

const CustomerDetailScreen = ({ match, history }) => {
  const userId = match.params.id

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const [orders, setOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [ordersError, setOrdersError] = useState(null)

  const dispatch = useDispatch()

  const userDetails = useSelector((state) => state.userDetails)
  const { loading, error, user } = userDetails

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const userUpdate = useSelector((state) => state.userUpdate)
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = userUpdate

  useEffect(() => {
    if (successUpdate) {
      Toast({ type: 'success', message: 'Customer updated successfully!' })
      dispatch({ type: USER_UPDATE_RESET })
      dispatch(getUserDetails(userId))
    }
    if (!user._id || user._id !== userId) {
      dispatch(getUserDetails(userId))
    } else {
      setFirstName(user.firstName || '')
      setLastName(user.lastName || '')
      setEmail(user.email || '')
      setPhone(user.phone || '')
    }
  }, [dispatch, userId, user._id, successUpdate])

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setOrdersLoading(true)
        const config = {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        }
        const { data } = await axios.get(`${apiUrl}/api/orders/user/${userId}`, config)
        setOrders(data)
        setOrdersLoading(false)
      } catch (err) {
        setOrdersError(
          err.response && err.response.data.message
            ? err.response.data.message
            : err.message
        )
        setOrdersLoading(false)
      }
    }
    if (userInfo && userInfo.token) {
      fetchOrders()
    }
  }, [userId, userInfo])

  const submitHandler = (e) => {
    e.preventDefault()
    dispatch(updateUser({ _id: userId, firstName, lastName, email, phone, isAdmin: user.isAdmin }))
  }

  const getStatusBadge = (status) => {
    const map = {
      Processing: 'warning',
      Shipped: 'info',
      Delivered: 'success',
      Cancelled: 'danger',
    }
    return map[status] || 'secondary'
  }

  return (
    <section className='minHeight100 my-5'>
      <div className='container'>
        <AdminActionBar
          loading={loading || loadingUpdate}
          name={[user.firstName, user.lastName].filter(Boolean).join(' ')}
          saveHandler={submitHandler}
          saveLabel='Save'
          backFallback='/admin/customers'
        />

        {loading ? (
          <Loader />
        ) : error ? (
          <Message variant='danger'>{error}</Message>
        ) : (
          <>
            <Row>
              <Col md={4}>
                <Card className='mb-4'>
                  <Card.Body>
                    <h5 className='mb-3'>Customer Info</h5>
                    <p className='mb-1'><strong>Name:</strong> {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'N/A'}</p>
                    <p className='mb-1'><strong>Phone:</strong> {user.phone || 'N/A'}</p>
                    <p className='mb-1'><strong>Email:</strong> {user.email || 'N/A'}</p>
                    <p className='mb-1'><strong>Joined:</strong> {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN') : 'N/A'}</p>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={8}>
                <Card className='mb-4'>
                  <Card.Body>
                    <h5 className='mb-3'>Edit Customer</h5>
                    {loadingUpdate && <Loader />}
                    {successUpdate && <Message variant='success'>Customer updated</Message>}
                    {errorUpdate && <Message variant='danger'>{errorUpdate}</Message>}

                    <Form onSubmit={submitHandler}>
                      <Row>
                        <Col md={6}>
                          <Form.Group controlId='firstName' className='mb-3'>
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                              type='text'
                              placeholder='First name'
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId='lastName' className='mb-3'>
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                              type='text'
                              placeholder='Last name'
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group controlId='email' className='mb-3'>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type='email'
                          placeholder='Email address'
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group controlId='phone' className='mb-3'>
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type='tel'
                          placeholder='Phone number'
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </Form.Group>

                      <Button type='submit' variant='dark'>
                        Update Customer
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card>
              <Card.Body>
                <h5 className='mb-3'>Orders ({orders.length})</h5>
                {ordersLoading ? (
                  <Loader />
                ) : ordersError ? (
                  <Message variant='danger'>{ordersError}</Message>
                ) : orders.length === 0 ? (
                  <Message variant='info'>No orders yet</Message>
                ) : (
                  <Table striped bordered hover responsive className='table-sm'>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Date</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Payment</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order._id}>
                          <td>{order.orderId || order._id.slice(-8)}</td>
                          <td>{new Date(order.createdAt).toLocaleDateString('en-IN')}</td>
                          <td>{order.orderItems?.length || 0}</td>
                          <td>{formatCurrency(order.totalPrice || 0)}</td>
                          <td>
                            <Badge bg={order.paymentStatus === 'completed' ? 'success' : 'warning'}>
                              {order.paymentStatus || 'Pending'}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={getStatusBadge(order.orderStatus)}>
                              {order.orderStatus || 'Processing'}
                            </Badge>
                          </td>
                          <td>
                            <Link to={`/order/${order._id}`} className='btn btn-sm btn-outline-dark'>
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </section>
  )
}

export default CustomerDetailScreen
